import React, { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'react-toastify'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import { printBoxBarcode } from '../_shared/printBarcode'
import {
  GlobalBoxesStyles, PageWrap, Topbar, Body,
  Toolbar, Search, Stats, NewBtn, RefreshBtn,
  ListWrap, BCard, BHead, BSeq, BName, StatusDot, BBarcode,
  BMeta, BMetaItem, BDriver, BActions, IconBtn,
  Empty, Loading,
  ModalOverlay, ModalBox, Label, Input, Select,
  StatusToggle, Switch, SeqHint,
  ModalActions, ModalBtn,
  ConfirmBox, ConfirmIcon, ConfirmTitle, ConfirmMessage, ConfirmBtn,
} from './style'

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function apiToLocal(b) {
  return {
    id: b.id,
    name: b.name || '',
    barcode: b.barcode || '',
    sequence: Number(b.sequence) || 0,
    driver_id: b.driver_id || null,
    driver_name: b.driver_name || b.driver?.name || null,
    accepts_no_driver: !!b.accepts_no_driver,
    is_active: b.is_active !== false,
    orders_count: Number(b.orders_count) || 0,
    orders_in_box_count: Number(b.orders_in_box_count) || 0,
    volumes_count: Number(b.volumes_count) || 0,
    volumes_coletados_count: Number(b.volumes_coletados_count) || 0,
    createdAt: b.createdAt || b.created_at || null,
    updatedAt: b.updatedAt || b.updated_at || null,
  }
}

const EMPTY_FORM = {
  id: null,
  name: '',
  barcode: '',
  driver_id: '',
  accepts_no_driver: false,
  is_active: true,
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SantoLogBoxes(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  const [boxes, setBoxes] = useState([])
  const [drivers, setDrivers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')
  const [form, setForm] = useState({ ...EMPTY_FORM, open: false })

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirmar', tone: 'warn' })
  const confirmResolveRef = useRef(null)
  function askConfirm({ title, message, confirmLabel = 'Confirmar', tone = 'warn' }) {
    return new Promise(resolve => {
      confirmResolveRef.current = resolve
      setConfirmDialog({ open: true, title, message, confirmLabel, tone })
    })
  }
  function closeConfirm(value) {
    setConfirmDialog(d => ({ ...d, open: false }))
    if (confirmResolveRef.current) {
      const r = confirmResolveRef.current
      confirmResolveRef.current = null
      r(Boolean(value))
    }
  }

  // ── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line
  useEffect(() => {
    if (auth === 'authorized') {
      fetchBoxes()
      fetchDrivers()
    }
  }, [auth]) // eslint-disable-line

  async function fetchBoxes() {
    setLoading(true)
    try {
      const res = await apiSantoLog.get('/box')
      const list = Array.isArray(res.data) ? res.data
        : (res.data?.data || res.data?.boxes || [])
      setBoxes(list.map(apiToLocal))
    } catch (err) {
      console.error('GET /box failed:', err)
      toast.error('Erro ao carregar boxes: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setLoading(false)
    }
  }
  async function fetchDrivers() {
    try {
      const res = await apiSantoLog.get('/driver')
      const list = Array.isArray(res.data) ? res.data
        : (res.data?.data || res.data?.drivers || [])
      setDrivers(list)
    } catch (err) {
      console.error('GET /driver failed:', err)
    }
  }

  // ── FORM ──────────────────────────────────────────────────────────────────
  function openCreate() {
    setForm({ ...EMPTY_FORM, open: true })
  }
  function openEdit(box) {
    setForm({
      open: true,
      id: box.id,
      name: box.name,
      barcode: box.barcode,
      driver_id: box.driver_id || '',
      accepts_no_driver: !!box.accepts_no_driver,
      is_active: !!box.is_active,
    })
  }
  function closeForm() { setForm(f => ({ ...f, open: false })) }
  const setField = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), [])

  function validate() {
    if (!form.name.trim()) { toast.warn('Informe o nome do box'); return false }
    if (!form.barcode.trim()) { toast.warn('Informe o código de barras'); return false }
    return true
  }

  async function saveBox() {
    if (!validate()) return
    // XOR: motorista OU sem-motorista, nunca os dois.
    // Sempre mandamos os DOIS campos no payload pra que o backend re-avalie
    // a regra (especialmente importante no PUT — ver E do contrato).
    const acceptsNoDriver = !!form.accepts_no_driver
    const payload = {
      name: form.name.trim(),
      barcode: form.barcode.trim(),
      driver_id: acceptsNoDriver ? null : (form.driver_id || null),
      accepts_no_driver: acceptsNoDriver,
      is_active: !!form.is_active,
    }
    setSaving(true)
    try {
      if (form.id) {
        const res = await apiSantoLog.put(`/box/${form.id}`, payload)
        const saved = res.data?.box || res.data?.data || res.data || {}
        const merged = apiToLocal({ ...saved, ...payload, id: form.id })
        // Preserva counts e sequence (PUT não muda esses)
        setBoxes(prev => prev.map(b => b.id === form.id ? {
          ...b, ...merged,
          orders_count: b.orders_count,
          orders_in_box_count: b.orders_in_box_count,
          volumes_count: b.volumes_count,
        } : b))
        toast.success('Box atualizado com sucesso')
      } else {
        const res = await apiSantoLog.post('/box', payload)
        const saved = res.data?.box || res.data?.data || res.data || {}
        const created = apiToLocal(saved)
        setBoxes(prev => [...prev, created].sort((a, b) => a.sequence - b.sequence))
        toast.success(`Box criado · sequência #${created.sequence}`)
      }
      closeForm()
    } catch (err) {
      console.error('Save box failed:', err)
      toast.error('Erro ao salvar box: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setSaving(false)
    }
  }

  // ── DELETE ────────────────────────────────────────────────────────────────
  async function deleteBox(box) {
    const warning = box.orders_in_box_count > 0
      ? `\n\n⚠ Este box possui ${box.orders_in_box_count} pedido${box.orders_in_box_count > 1 ? 's' : ''} em uso. Os scans serão removidos.`
      : ''
    const ok = await askConfirm({
      title: 'Remover box',
      message: `Tem certeza que deseja remover "${box.name}" (#${box.sequence})?${warning}`,
      confirmLabel: 'Remover',
      tone: 'danger',
    })
    if (!ok) return
    setSaving(true)
    try {
      await apiSantoLog.delete(`/box/${box.id}`)
      setBoxes(prev => prev.filter(b => b.id !== box.id))
      toast.success('Box removido')
    } catch (err) {
      console.error('DELETE /box/:id failed:', err)
      toast.error('Erro ao remover box: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setSaving(false)
    }
  }

  // ── FILTER ────────────────────────────────────────────────────────────────
  const q = query.trim().toLowerCase()
  const byName = (a, b) =>
    (a.name || '').localeCompare(b.name || '', 'pt-BR', { numeric: true, sensitivity: 'base' })
  const filtered = (!q ? [...boxes] : boxes.filter(b => {
    return b.name.toLowerCase().includes(q)
      || b.barcode.toLowerCase().includes(q)
      || (b.driver_name || '').toLowerCase().includes(q)
      || String(b.sequence).includes(q)
  })).sort(byName)

  const totalInBox = boxes.reduce((sum, b) => sum + (b.orders_in_box_count || 0), 0)
  const activeCount = boxes.filter(b => b.is_active).length

  // Helper pra mostrar o nome do motorista no card
  const driverNameById = (id) => drivers.find(d => d.id === id)?.name || null

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Boxes">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalBoxesStyles />
          <PageWrap>
            <Topbar>
              <h2>🗂️ Cadastro de Boxes</h2>
              <span className="crumb">Posições físicas onde os volumes são organizados</span>
            </Topbar>

            <Body>
              <Toolbar>
                <Search
                  placeholder="Buscar por nome, código, motorista, sequência…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                <Stats>
                  <span><strong>{boxes.length}</strong>boxes</span>
                  <span><strong>{activeCount}</strong>ativos</span>
                  <span><strong>{totalInBox}</strong>pedidos no box</span>
                </Stats>
                <RefreshBtn onClick={fetchBoxes} disabled={loading}>
                  {loading ? '…' : '↻ Atualizar'}
                </RefreshBtn>
                <NewBtn onClick={openCreate} disabled={saving}>+ Novo box</NewBtn>
              </Toolbar>

              <ListWrap>
                {loading && !boxes.length && <Loading>Carregando boxes…</Loading>}

                {!loading && !boxes.length && (
                  <Empty>
                    Nenhum box cadastrado.<br />
                    Clique em <strong>"+ Novo box"</strong> para começar.
                  </Empty>
                )}

                {!loading && boxes.length > 0 && filtered.length === 0 && (
                  <Empty>Nenhum box corresponde à busca.</Empty>
                )}

                {filtered.map(b => {
                  const driver = b.driver_name || driverNameById(b.driver_id)
                  return (
                    <BCard
                      key={b.id}
                      $hasOrders={b.orders_in_box_count > 0}
                      $inactive={!b.is_active}
                    >
                      <BHead>
                        <BSeq>#{b.sequence}</BSeq>
                        <BName title={b.name}>{b.name}</BName>
                        <StatusDot $on={b.is_active} title={b.is_active ? 'Ativo' : 'Inativo'} />
                      </BHead>

                      <BBarcode>
                        <span className="lbl">Barcode</span>
                        <span className="code">{b.barcode || '—'}</span>
                      </BBarcode>

                      <BMeta>
                        <BMetaItem $highlight={b.orders_in_box_count > 0}>
                          <div className="l">Pedidos no box</div>
                          <div className="v">{b.orders_in_box_count}</div>
                          <div className="sub">
                            {b.volumes_coletados_count ?? b.volumes_count ?? 0} volume
                            {(b.volumes_coletados_count ?? b.volumes_count ?? 0) !== 1 ? 's' : ''} bipado
                            {(b.volumes_coletados_count ?? b.volumes_count ?? 0) !== 1 ? 's' : ''}
                          </div>
                        </BMetaItem>
                        <BMetaItem>
                          <div className="l">Histórico</div>
                          <div className="v">{b.orders_count}</div>
                          <div className="sub">pedido{b.orders_count !== 1 ? 's' : ''} no total</div>
                        </BMetaItem>
                      </BMeta>

                      <BDriver className={!driver && !b.accepts_no_driver ? 'empty' : ''}>
                        <span className="lbl">Motorista:</span>
                        {b.accepts_no_driver ? (
                          <span className="name" title="Este box recebe pedidos sem motorista">👤 Sem Motorista</span>
                        ) : driver
                          ? <span className="name">👤 {driver}</span>
                          : <span>Sem motorista vinculado</span>}
                      </BDriver>

                      <BActions>
                        <IconBtn
                          onClick={() => {
                            if (!b.barcode) { toast.warn('Box sem código de barras.'); return }
                            const r = printBoxBarcode(b.barcode)
                            if (!r.ok && r.error === 'popup_blocked') {
                              toast.warn('Permita pop-ups neste site para imprimir.')
                            }
                          }}
                          disabled={saving}
                          title="Imprimir código de barras (A4)"
                        >🖨</IconBtn>
                        <IconBtn onClick={() => openEdit(b)} disabled={saving} title="Editar box">✏️</IconBtn>
                        <IconBtn $danger onClick={() => deleteBox(b)} disabled={saving} title="Remover box">🗑</IconBtn>
                      </BActions>
                    </BCard>
                  )
                })}
              </ListWrap>
            </Body>
          </PageWrap>

          {/* ─── MODAL CRIAR/EDITAR ─────────────────────────────────────── */}
          <ModalOverlay $visible={form.open}>
            <ModalBox>
              <h3>{form.id ? 'Editar box' : 'Novo box'}</h3>
              <p>
                {form.id
                  ? 'Edite o nome, código de barras ou motorista vinculado.'
                  : 'Cadastre um novo box físico. A sequência será atribuída automaticamente pelo sistema.'}
              </p>

              <Label>Nome *</Label>
              <Input
                autoFocus
                value={form.name}
                onChange={e => { const v = e.target.value; setField('name', v) }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !saving) saveBox()
                  if (e.key === 'Escape') closeForm()
                }}
                placeholder="Ex: Box 01 · Esquina A"
                disabled={saving}
                maxLength={80}
              />

              <Label>Código de barras *</Label>
              <Input
                value={form.barcode}
                onChange={e => { const v = e.target.value; setField('barcode', v) }}
                placeholder="Ex: BX001"
                disabled={saving}
                maxLength={100}
              />

              <Label>Motorista <span className="opt">(opcional — boxes do motorista têm prioridade na sugestão)</span></Label>
              <Select
                value={form.driver_id || ''}
                onChange={e => {
                  const v = e.target.value
                  setField('driver_id', v)
                  // Selecionar motorista desliga accepts_no_driver (XOR)
                  if (v) setField('accepts_no_driver', false)
                }}
                disabled={saving || form.accepts_no_driver}
              >
                <option value="">— Nenhum motorista —</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Select>
              {form.accepts_no_driver && (
                <div style={{ fontSize: 11, color: '#5a6370', marginTop: -8, marginBottom: 10 }}>
                  Desligue "Aceita pedidos sem motorista" para vincular um motorista.
                </div>
              )}

              <Label>Aceita pedidos sem motorista</Label>
              <StatusToggle>
                <div>
                  <div className="lbl">
                    {form.accepts_no_driver
                      ? '🆓 Box destinado a pedidos SEM motorista'
                      : 'Box normal (vinculável a motorista)'}
                  </div>
                  <span className="hint">
                    {form.accepts_no_driver
                      ? 'Pedidos com driver_id IS NULL serão direcionados a este box. Não pode ter motorista vinculado.'
                      : 'Quando ligado, este box só recebe pedidos sem motorista — e desvincula qualquer motorista atual.'}
                  </span>
                </div>
                <Switch
                  $on={form.accepts_no_driver}
                  onClick={() => {
                    const next = !form.accepts_no_driver
                    setField('accepts_no_driver', next)
                    // Ligar accepts_no_driver desvincula motorista (XOR)
                    if (next) setField('driver_id', '')
                  }}
                  type="button"
                  disabled={saving}
                />
              </StatusToggle>

              <Label>Status</Label>
              <StatusToggle>
                <div>
                  <div className="lbl">{form.is_active ? 'Box ativo' : 'Box inativo'}</div>
                  <span className="hint">
                    {form.is_active
                      ? 'Disponível para receber volumes e ser sugerido'
                      : 'Não aparece na sugestão e recusa scans'}
                  </span>
                </div>
                <Switch
                  $on={form.is_active}
                  onClick={() => setField('is_active', !form.is_active)}
                  type="button"
                  disabled={saving}
                />
              </StatusToggle>

              {!form.id && (
                <SeqHint>
                  💡 A <strong>sequência</strong> é atribuída automaticamente pelo banco (AUTO_INCREMENT).
                  Use-a como referência da ordem física dos boxes na esteira.
                </SeqHint>
              )}

              <ModalActions>
                <ModalBtn onClick={closeForm} disabled={saving}>Cancelar</ModalBtn>
                <ModalBtn
                  $primary
                  onClick={saveBox}
                  disabled={saving || !form.name.trim() || !form.barcode.trim()}
                >
                  {saving ? 'Salvando…' : (form.id ? 'Salvar alterações' : 'Cadastrar box')}
                </ModalBtn>
              </ModalActions>
            </ModalBox>
          </ModalOverlay>

          {/* ─── CONFIRM MODAL ───────────────────────────────────────── */}
          <ModalOverlay $visible={confirmDialog.open}>
            <ConfirmBox $tone={confirmDialog.tone}>
              <ConfirmIcon $tone={confirmDialog.tone}>
                {confirmDialog.tone === 'danger' ? '⚠' : '?'}
              </ConfirmIcon>
              <ConfirmTitle>{confirmDialog.title}</ConfirmTitle>
              <ConfirmMessage>{confirmDialog.message}</ConfirmMessage>
              <ModalActions>
                <ConfirmBtn onClick={() => closeConfirm(false)}>Cancelar</ConfirmBtn>
                <ConfirmBtn $primary $tone={confirmDialog.tone} onClick={() => closeConfirm(true)}>
                  {confirmDialog.confirmLabel}
                </ConfirmBtn>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        </>
      )}
    </Dashboard>
  )
}
